import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { format0 } from 'utils/number';
import type { ComparisonMetricsData, SlotCategoryMetric } from './types';
import style from './comparison-metrics.module.scss';
import {
  SLOT_CATEGORY_KEYS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
  wellStatusCountKey,
  SLOT_CATEGORY_LABELS,
} from 'utils/slot-categories';

interface ComparisonMetricsProps {
  metrics: ComparisonMetricsData | null;
  isLoading: boolean;
}

interface ReconciliationRow {
  label: string;
  metric: SlotCategoryMetric;
}

const EMPTY_METRIC: SlotCategoryMetric = {
  countOnlyA: 0,
  countOnlyB: 0,
  matched: 0,
  failed: 0,
};

const CATEGORY_ROWS: { key: string; label: string }[] = [
  ...SLOT_CATEGORY_KEYS.map((key) => ({
    key,
    label: SLOT_CATEGORY_LABELS[key],
  })),
  { key: 'undefined', label: 'Undefined' },
];

const PLACEHOLDER = '--';

/**
 * "In Both" = slots present in both evaluations, i.e. matched by identity —
 * the sum of within-threshold (matched) and over-threshold (failed) slots.
 * @param {SlotCategoryMetric} metric - The per-category counts
 * @return {number} Count of slots present in both evaluations
 */
const inBoth = (metric: SlotCategoryMetric): number =>
  metric.matched + metric.failed;

/**
 * True when a category has no properties on either side (nothing In Both, Only in
 * Origin, or Only in Target).
 * @param {SlotCategoryMetric} metric - The per-category counts
 * @return {boolean} Whether the category is empty
 */
const isEmptyMetric = (metric: SlotCategoryMetric): boolean =>
  inBoth(metric) === 0 && metric.countOnlyA === 0 && metric.countOnlyB === 0;

/**
 * Builds the reconciliation rows: an "All properties" total followed by one row
 * per reserve category. The "Undefined" bucket (properties carrying no reserve
 * category) is only shown when populated — an empty Undefined row is hidden — and so is a
 * Create New Well status, which most runs carry none of (INS-13303).
 * @param {ComparisonMetricsData} metrics - The metrics data from the API
 * @return {ReconciliationRow[]} Ordered reconciliation rows
 */
const buildRows = (metrics: ComparisonMetricsData): ReconciliationRow[] => {
  const rows: ReconciliationRow[] = [
    { label: 'All properties', metric: metrics.totalSlotCounts },
  ];

  CATEGORY_ROWS.forEach(({ key, label }) => {
    // The response is camel-cased on the way out (JsonKeyTransformer), so `pre_permit` arrives as
    // `prePermit`; single-word keys are unchanged.
    const metric =
      metrics.slotCountsByCategory[wellStatusCountKey(key)] ?? EMPTY_METRIC;
    const optional =
      key === 'undefined' ||
      (WELL_STATUS_SLOT_CATEGORY_KEYS as readonly string[]).includes(key);
    if (optional && isEmptyMetric(metric)) return;
    rows.push({ label, metric });
  });

  return rows;
};

/**
 * Reserve category reconciliation: for each reserve category, how many
 * properties appear in both evaluations vs only one side. Collapsible, mirrors
 * the other result sections.
 * @param {ComparisonMetricsProps} props - The metrics data and loading flag
 * @return {JSX.Element} The reconciliation section
 */
export const ComparisonMetrics = ({
  metrics,
  isLoading,
}: ComparisonMetricsProps) => {
  const [expanded, setExpanded] = useState(true);
  const ready = !isLoading && !!metrics;
  const rows = ready ? buildRows(metrics) : [];

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={style.card}
    >
      <AccordionSummary>
        <h3 className={style.heading}>Reserve category reconciliation</h3>
      </AccordionSummary>
      <AccordionDetails>
        <p className={style.description}>
          Properties are matched by identity and counted under their Origin
          reserve category. “In Both” means the property is present in both
          evaluations; a property present on only one side shows under “Only in
          Origin” or “Only in Target”. A property reclassified between the two
          evaluations (a different reserve category on each side) is still “In
          Both”, counted once under its Origin category — so category rows need
          not sum to the property totals above.
        </p>
        <table className={style.table}>
          <thead>
            <tr>
              <th className={style.rowHeader} scope="col">
                Reserve Category
              </th>
              <th className={style.value} scope="col">
                In Both
              </th>
              <th className={style.value} scope="col">
                Only in Origin
              </th>
              <th className={style.value} scope="col">
                Only in Target
              </th>
            </tr>
          </thead>
          <tbody>
            {ready ? (
              rows.map((row) => (
                <tr key={row.label}>
                  <th className={style.rowHeader} scope="row">
                    {row.label}
                  </th>
                  <td className={style.value}>{format0(inBoth(row.metric))}</td>
                  <td className={style.value}>
                    {format0(row.metric.countOnlyA)}
                  </td>
                  <td className={style.value}>
                    {format0(row.metric.countOnlyB)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <th className={style.rowHeader} scope="row">
                  All properties
                </th>
                <td className={style.value}>{PLACEHOLDER}</td>
                <td className={style.value}>{PLACEHOLDER}</td>
                <td className={style.value}>{PLACEHOLDER}</td>
              </tr>
            )}
          </tbody>
        </table>
      </AccordionDetails>
    </Accordion>
  );
};
